/**
 * R13-F — Super-Admin dashboard.
 * Routes: /admin
 * Access:  platform_role === "super_admin" (backend enforces; we also guard route-level).
 *
 * Surfaces:
 *   - Rooms list (24 seeded + any others), with per-row "Assign Keyholder"
 *   - Assign Keyholder modal — picks from registered users
 *   - "Pending Promotions" placeholder (Phase 2, intentional stub)
 *
 * Testids (per spec):
 *   - admin-dashboard
 *   - admin-rooms-list
 *   - admin-assign-keyholder-{slug}
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth-context";
import { toast } from "sonner";
import { Loader2, Shield, Users, ExternalLink, Crown, X, Search, FileText, Filter, Calendar, ChevronDown, ChevronUp, Bot, MessageSquare, Disc3, Download, DollarSign, ArrowUpDown } from "lucide-react";
import DarkSelect from "../components/DarkSelect";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authzError, setAuthzError] = useState(null);
  const [assignFor, setAssignFor] = useState(null); // room row when modal open

  const fetchRooms = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/rooms`, { credentials: "include" });
      if (r.status === 403 || r.status === 401) {
        setAuthzError("Super-admin only. Ask the platform owner to promote your account.");
        setLoading(false);
        return;
      }
      const j = await r.json();
      setRooms(j.rooms || []);
      setLoading(false);
    } catch (err) {
      console.error("[admin] failed to fetch rooms", err);
      toast.error("Failed to load admin dashboard");
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  // Route guard: bounce non-authed users to login
  useEffect(() => {
    if (user === false) nav("/login", { replace: true });
  }, [user, nav]);

  const seedRooms = useMemo(() => rooms.filter((r) => r.is_seed), [rooms]);
  const otherRooms = useMemo(() => rooms.filter((r) => !r.is_seed), [rooms]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050508] text-white grid place-items-center">
        <Loader2 className="w-7 h-7 animate-spin text-[#00F0FF]" />
      </div>
    );
  }
  if (authzError) {
    return (
      <div className="min-h-screen bg-[#050508] text-white grid place-items-center p-6">
        <div className="max-w-md p-7 rounded-xl border border-white/10 bg-[#0c0c12]" data-testid="admin-403">
          <Shield className="w-7 h-7 text-amber-400 mb-3" />
          <h1 className="text-xl font-bold mb-2">Access restricted</h1>
          <p className="text-white/65 text-sm">{authzError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050508] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#080810]/85 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Shield className="w-5 h-5 text-[#00F0FF]" />
          <h1 className="text-lg font-bold tracking-tight">Full Spectrum Center — Super-Admin</h1>
          <span className="ml-3 text-[10px] uppercase tracking-[0.18em] text-white/45 border border-white/15 px-2 py-1 rounded">R13</span>
          <div className="ml-auto text-xs text-white/55">{user?.display_name || user?.email}</div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-10">
        <section>
          <div className="flex items-baseline gap-3 mb-4">
            <h2 className="text-base font-bold uppercase tracking-[0.18em] text-white/85">Seed rooms</h2>
            <span className="text-xs text-white/45">{seedRooms.length} keyholder slots</span>
          </div>
          <RoomsTable
            rooms={seedRooms}
            onAssign={setAssignFor}
            emptyText="Seed rooms not yet boostrapped. Restart backend to seed."
            testid="admin-rooms-list"
          />
        </section>

        {otherRooms.length > 0 && (
          <section>
            <div className="flex items-baseline gap-3 mb-4">
              <h2 className="text-base font-bold uppercase tracking-[0.18em] text-white/55">Other rooms</h2>
              <span className="text-xs text-white/45">{otherRooms.length}</span>
            </div>
            <RoomsTable rooms={otherRooms} onAssign={setAssignFor} testid="admin-rooms-other" />
          </section>
        )}

        <section className="rounded-xl border border-dashed border-white/12 p-6 bg-white/[0.02]">
          <div className="flex items-center gap-3 text-white/55">
            <Users className="w-4 h-4" />
            <h3 className="text-sm font-bold uppercase tracking-[0.18em]">Pending Promotions</h3>
          </div>
          <p className="text-xs text-white/40 mt-2">
            Empty for now — this is where cohost → keyholder requests will surface in a future round.
          </p>
        </section>

        {/* R16-G — System-wide ended-room reports. Super-admin only. */}
        <AdminReportsSection />
      </main>

      {assignFor && (
        <AssignKeyholderModal
          room={assignFor}
          onClose={() => setAssignFor(null)}
          onAssigned={() => { setAssignFor(null); fetchRooms(); }}
        />
      )}
    </div>
  );
}


function RoomsTable({ rooms, onAssign, emptyText, testid }) {
  if (rooms.length === 0) {
    return (
      <div className="rounded-xl border border-white/10 p-6 text-center text-white/50 text-sm" data-testid={`${testid}-empty`}>
        {emptyText || "Nothing here."}
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-white/10 overflow-hidden" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-white/[0.04] text-white/55 text-[11px] uppercase tracking-[0.14em]">
          <tr>
            <th className="text-left py-2.5 px-4">Room</th>
            <th className="text-left py-2.5 px-4">Handle</th>
            <th className="text-left py-2.5 px-4">Owner & support</th>
            <th className="text-left py-2.5 px-4">Status</th>
            <th className="text-right py-2.5 px-4"></th>
          </tr>
        </thead>
        <tbody>
          {rooms.map((r) => (
            <tr key={r.room_id} className="border-t border-white/5 hover:bg-white/[0.02]"
                data-testid={`admin-room-row-${r.slug || r.room_id}`}>
              <td className="py-3 px-4">
                <div className="font-semibold">{r.name}</div>
                <div className="text-[10px] text-white/35 mt-0.5">{r.slug ? `Community: ${r.slug}` : "Private room"}</div>
              </td>
              <td className="py-3 px-4">
                {r.community_handle_url ? (
                  <a href={r.community_handle_url} target="_blank" rel="noreferrer"
                     className="text-[#00F0FF] hover:underline text-xs inline-flex items-center gap-1">
                    {prettyHandle(r.community_handle_url)} <ExternalLink className="w-3 h-3" />
                  </a>
                ) : <span className="text-white/30 text-xs">—</span>}
              </td>
              <td className="py-3 px-4">
                <div className="text-xs"><span className="text-white/40">Owner:</span> {r.owner_username || "Unassigned"}</div>
                {r.keyholder_username && <div className="inline-flex items-center gap-1.5 text-[11px] text-amber-300 mt-1"><Crown className="w-3 h-3" /> Keyholder: {r.keyholder_username}</div>}
                {r.current_dj_username && <div className="text-[11px] text-[#FF007F] mt-1">DJ: {r.current_dj_username}</div>}
                {(r.cohost_usernames || []).length > 0 && <div className="text-[11px] text-[#00F0FF] mt-1">Cohosts: {r.cohost_usernames.join(", ")}</div>}
              </td>
              <td className="py-3 px-4">
                <StatusPill status={r.status} />
                <div className="text-[10px] text-white/40 mt-1">{r.live_participant_count || 0} live participant{r.live_participant_count === 1 ? "" : "s"}</div>
              </td>
              <td className="py-3 px-4 text-right">
                <button
                  onClick={() => onAssign(r)}
                  className="text-[10px] uppercase tracking-[0.14em] font-bold px-3 py-1.5 rounded border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/12 transition"
                  data-testid={`admin-assign-keyholder-${r.slug || r.room_id}`}
                >
                  {r.keyholder_user ? "Reassign" : "Assign Keyholder"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    inactive:  { bg: "bg-white/8",    fg: "text-white/55", label: "Inactive" },
    closed:    { bg: "bg-white/8",    fg: "text-white/55", label: "Closed" },
    available: { bg: "bg-emerald-500/15", fg: "text-emerald-300", label: "Available" },
    live:      { bg: "bg-[#00F0FF]/15",   fg: "text-[#00F0FF]",   label: "Live" },
    ended:     { bg: "bg-rose-500/15",    fg: "text-rose-300",    label: "Ended" },
  };
  const s = map[status] || map.live;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.14em] ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  );
}

function prettyHandle(url) {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, "") + u.pathname.replace(/\/$/, "");
  } catch { return url; }
}


function AssignKeyholderModal({ room, onClose, onAssigned }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/users`, { credentials: "include" });
        const j = await r.json();
        setUsers(j.users || []);
      } catch (err) {
        console.error("[admin] users fetch failed", err);
        toast.error("Failed to load users");
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users.slice(0, 50);
    return users.filter((u) =>
      (u.display_name || "").toLowerCase().includes(needle) ||
      (u.email || "").toLowerCase().includes(needle) ||
      (u.discord_id || "").toLowerCase().includes(needle)
    ).slice(0, 50);
  }, [users, q]);

  const confirm = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/rooms/${encodeURIComponent(room.room_id)}/keyholder`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: picked.user_id }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || "Assign failed");
        setBusy(false);
        return;
      }
      toast.success(`Assigned ${picked.display_name} as Keyholder of ${room.name}`);
      onAssigned();
    } catch (err) {
      console.error("[admin] assign failed", err);
      toast.error("Assign failed");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-2xl border border-white/12 bg-[#0c0c14] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-assign-modal"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45">Assign Keyholder</div>
            <h2 className="text-base font-bold mt-1">{room.name}</h2>
          </div>
          <button onClick={onClose} className="text-white/55 hover:text-white" data-testid="admin-assign-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="p-5 space-y-3">
          <label className="relative block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
            <input
              autoFocus
              type="text"
              placeholder="Search by name, email, or Discord ID"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#00F0FF]/40"
              data-testid="admin-assign-search"
            />
          </label>

          <div className="max-h-64 overflow-y-auto rounded-lg border border-white/8 divide-y divide-white/5">
            {filtered.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-white/35">No matching users.</div>
            )}
            {filtered.map((u) => (
              <button
                key={u.user_id}
                onClick={() => setPicked(u)}
                className={`w-full text-left px-3 py-2 hover:bg-white/[0.04] transition ${picked?.user_id === u.user_id ? "bg-[#00F0FF]/8 border-l-2 border-[#00F0FF]" : ""}`}
                data-testid={`admin-assign-user-${u.user_id}`}
              >
                <div className="text-sm">{u.display_name}</div>
                <div className="text-[10px] text-white/35">{u.email || u.discord_id || u.user_id.slice(0, 12)}</div>
              </button>
            ))}
          </div>
        </div>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/8">
          <button onClick={onClose} className="px-3 py-1.5 text-xs uppercase tracking-[0.14em] text-white/55 hover:text-white">Cancel</button>
          <button
            disabled={!picked || busy}
            onClick={confirm}
            className="px-4 py-1.5 text-xs uppercase tracking-[0.14em] font-bold rounded bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="admin-assign-confirm"
          >
            {busy ? "Assigning…" : "Confirm"}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ============================================================================
// R16-G — System-wide ended-room reports for super-admin oversight.
// ============================================================================

function AdminReportsSection() {
  const [reports, setReports] = useState([]);
  const [keyholders, setKeyholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [range, setRange] = useState("30d");
  const [keyholderFilter, setKeyholderFilter] = useState("");
  const [roomFilter, setRoomFilter] = useState("");
  const [openReportRoomId, setOpenReportRoomId] = useState(null);
  // R18-12 — Sort by estimated cost. Default unsorted (server returns by
  // ended_at desc); click header to toggle desc → asc → off.
  const [costSort, setCostSort] = useState(null); // null | "desc" | "asc"

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const qp = new URLSearchParams();
      qp.set("range", range);
      if (keyholderFilter) qp.set("keyholder_user_id", keyholderFilter);
      if (roomFilter) qp.set("room_id", roomFilter);
      const r = await fetch(`${API}/api/admin/reports?${qp.toString()}`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setReports(data.reports || []);
      setKeyholders(data.filter_options?.keyholders || []);
      setError("");
    } catch (e) {
      setError(e?.message || "Couldn't load reports.");
    } finally {
      setLoading(false);
    }
  }, [range, keyholderFilter, roomFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  // R18-12 — Client-side sort + footer total. Memoize so toggling sort
  // doesn't re-render every row.
  const visibleReports = useMemo(() => {
    if (!costSort) return reports;
    const copy = [...reports];
    copy.sort((a, b) => {
      const ca = Number(a?.stats?.estimated_cost_usd || 0);
      const cb = Number(b?.stats?.estimated_cost_usd || 0);
      return costSort === "asc" ? ca - cb : cb - ca;
    });
    return copy;
  }, [reports, costSort]);

  const totalCost = useMemo(
    () => reports.reduce((sum, r) => sum + Number(r?.stats?.estimated_cost_usd || 0), 0),
    [reports],
  );

  const cycleCostSort = () => setCostSort((p) => (p === "desc" ? "asc" : p === "asc" ? null : "desc"));

  return (
    <section data-testid="admin-reports-section">
      <div className="flex items-baseline gap-3 mb-4">
        <h2 className="text-base font-bold uppercase tracking-[0.18em] text-white/85">All room reports</h2>
        <span className="text-xs text-white/45" data-testid="admin-reports-count">{reports.length} ended rooms</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4 px-4 py-3 rounded-lg bg-white/[0.025] border border-white/8">
        <div className="flex flex-col gap-1 min-w-[160px]">
          <label className="text-[10px] uppercase tracking-[0.18em] text-white/45 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Date range
          </label>
          <DarkSelect
            value={range}
            onChange={setRange}
            options={[
              { value: "7d", label: "Last 7 days" },
              { value: "30d", label: "Last 30 days" },
              { value: "90d", label: "Last 90 days" },
              { value: "all", label: "All time" },
            ]}
            testid="admin-reports-filter-date"
          />
        </div>
        <div className="flex flex-col gap-1 min-w-[220px]">
          <label className="text-[10px] uppercase tracking-[0.18em] text-white/45 flex items-center gap-1">
            <Crown className="w-3 h-3" /> Keyholder
          </label>
          <DarkSelect
            value={keyholderFilter}
            onChange={setKeyholderFilter}
            options={[
              { value: "", label: "All keyholders" },
              ...keyholders.map((k) => ({ value: k.user_id, label: k.display_name })),
            ]}
            testid="admin-reports-filter-keyholder"
          />
        </div>
        <div className="flex flex-col gap-1 min-w-[220px] flex-1">
          <label className="text-[10px] uppercase tracking-[0.18em] text-white/45 flex items-center gap-1">
            <Filter className="w-3 h-3" /> Room
          </label>
          <input
            type="text"
            value={roomFilter}
            onChange={(e) => setRoomFilter(e.target.value)}
            placeholder="Filter by room id…"
            className="px-3 py-2 bg-[#0c0c12] border border-white/10 rounded text-sm text-white placeholder-white/30 focus:border-[#00F0FF]/60 focus:outline-none"
            data-testid="admin-reports-filter-room"
          />
        </div>
        {(keyholderFilter || roomFilter || range !== "30d") && (
          <button
            onClick={() => { setKeyholderFilter(""); setRoomFilter(""); setRange("30d"); }}
            className="px-3 py-2 text-[10px] uppercase tracking-[0.16em] text-white/55 hover:text-white border border-white/10 rounded"
            data-testid="admin-reports-clear-filters"
          >
            Clear
          </button>
        )}
      </div>

      {loading && (
        <div className="text-center text-white/40 py-8 text-sm">
          <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" /> Loading…
        </div>
      )}
      {!loading && error && (
        <div className="text-center text-red-300 py-6 text-sm" data-testid="admin-reports-error">{error}</div>
      )}
      {!loading && !error && reports.length === 0 && (
        <div className="text-center text-white/40 py-12 text-sm border border-dashed border-white/10 rounded-lg" data-testid="admin-reports-empty">
          No ended rooms in this range.
        </div>
      )}
      {!loading && !error && reports.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-white/[0.06]" data-testid="admin-reports-list">
          {/* R18-12 — Sortable column header + footer total for Estimated Cost.
              Layout: room (flex), date, hours, stats, est. cost, action. */}
          <div className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] items-center gap-3 px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-white/45 bg-white/[0.02] border-b border-white/[0.06]"
               data-testid="admin-reports-table-header">
            <div>Room / keyholder</div>
            <div className="text-right hidden sm:block">Ended</div>
            <div className="text-right hidden sm:block">Hrs</div>
            <div className="text-right hidden md:block">Stats</div>
            <button
              type="button"
              onClick={cycleCostSort}
              title="Estimated LiveKit Cloud egress cost. Self-hosted rooms show $0.00."
              className="text-right inline-flex items-center gap-1 hover:text-white"
              data-testid="admin-reports-cost-header"
            >
              <DollarSign className="w-3 h-3" /> Est. Cost
              {costSort === "desc" ? <ChevronDown className="w-3 h-3" /> :
                costSort === "asc" ? <ChevronUp className="w-3 h-3" /> :
                <ArrowUpDown className="w-3 h-3 opacity-50" />}
            </button>
            <div></div>
          </div>
          <ul className="divide-y divide-white/[0.04]">
            {visibleReports.map((r) => (
              <li
                key={r.room_id}
                className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] items-center gap-3 px-4 py-2.5 bg-white/[0.015] hover:bg-white/[0.04]"
                data-testid={`admin-reports-row-${r.room_id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate flex items-center gap-2">
                    {r.name}
                    {r.keyholder && (
                      <span className="text-[10px] text-amber-300/80 font-normal">· {r.keyholder.display_name}</span>
                    )}
                  </div>
                  <div className="text-[10px] text-white/40 font-mono mt-0.5">
                    <span className="opacity-65">{r.room_id}</span>
                  </div>
                </div>
                <div className="text-[10px] text-white/55 hidden sm:block text-right whitespace-nowrap">
                  {r.ended_at ? new Date(r.ended_at).toLocaleDateString() : "—"}
                </div>
                <div className="text-[10px] text-white/55 hidden sm:block text-right whitespace-nowrap">
                  {r.duration_hours !== null ? `${r.duration_hours}h` : "—"}
                </div>
                <div className="text-[10px] text-white/55 hidden md:flex items-center gap-2 justify-end whitespace-nowrap">
                  <span className="text-white/70 flex items-center gap-0.5">
                    <Users className="w-3 h-3" /> {r.stats.participants_total}
                  </span>
                  <span className="text-white/70 flex items-center gap-0.5">
                    <MessageSquare className="w-3 h-3" /> {r.stats.chat_messages}
                  </span>
                  <span className="text-amber-300/75 flex items-center gap-0.5">
                    <Bot className="w-3 h-3" /> {r.stats.bot_escalations}
                  </span>
                  {r.stats.has_recording && (
                    <span className="text-cyan-300/80 flex items-center gap-0.5">
                      <Disc3 className="w-3 h-3" /> rec
                    </span>
                  )}
                </div>
                <div className="text-right font-mono text-xs tabular-nums text-white/85 whitespace-nowrap" data-testid={`admin-reports-cost-${r.room_id}`}>
                  ${Number(r.stats?.estimated_cost_usd || 0).toFixed(2)}
                </div>
                <button
                  onClick={() => setOpenReportRoomId(r.room_id)}
                  className="px-3 py-1.5 rounded text-[10px] font-bold uppercase tracking-[0.14em] bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 flex items-center gap-1.5 shrink-0"
                  data-testid={`admin-reports-view-${r.room_id}`}
                >
                  <FileText className="w-3 h-3 text-[#00F0FF]" /> View
                </button>
              </li>
            ))}
          </ul>
          {/* R18-12 — Footer total cost. */}
          <div className="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-4 py-2 bg-white/[0.03] border-t border-white/[0.08] text-[11px] uppercase tracking-[0.16em] text-white/65"
               data-testid="admin-reports-cost-footer">
            <div className="font-bold text-white/85">Total estimated cost</div>
            <div className="text-right font-mono tabular-nums text-white text-sm" data-testid="admin-reports-cost-total">
              ${totalCost.toFixed(2)}
            </div>
            <div className="text-[10px] text-white/40 normal-case tracking-normal hidden sm:block">
              {reports.length} room{reports.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
      )}

      {openReportRoomId && (
        <AdminReportModal
          roomId={openReportRoomId}
          onClose={() => setOpenReportRoomId(null)}
        />
      )}
    </section>
  );
}

function AdminReportModal({ roomId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/admin/rooms/${roomId}/report`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e?.message || "Couldn't load report."));
    return () => { cancelled = true; };
  }, [roomId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/75 grid place-items-center px-6 py-10" onClick={onClose} data-testid={`admin-report-modal-${roomId}`}>
      <div
        className="w-full max-w-2xl max-h-[88vh] overflow-y-auto bg-[#0c0c12] border border-white/10 rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center gap-3 px-5 py-4 border-b border-white/8 sticky top-0 bg-[#0c0c12]">
          <FileText className="w-4 h-4 text-[#00F0FF]" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.16em] text-white/45">Room report</div>
            <h2 className="text-base font-bold truncate">{data?.room?.name || roomId}</h2>
          </div>
          <button onClick={onClose} className="text-white/55 hover:text-white" data-testid="admin-report-modal-close">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="p-5 space-y-4">
          {error && <div className="text-sm text-red-300">{error}</div>}
          {!data && !error && (
            <div className="text-center py-12 text-white/40 text-sm">
              <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" /> Loading report…
            </div>
          )}
          {data && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <ReportStat label="Created" value={data.room?.created_at ? new Date(data.room.created_at).toLocaleString() : "—"} />
                <ReportStat label="Ended" value={data.room?.ended_at ? new Date(data.room.ended_at).toLocaleString() : "—"} />
                <ReportStat label="Duration" value={data.duration_seconds ? `${(data.duration_seconds/3600).toFixed(2)} h` : "—"} />
                <ReportStat label="Reopens" value={String(data.reopen_count || 0)} />
                <ReportStat label="Participants total" value={String((data.participants || []).length)} />
                <ReportStat label="DJ assigns" value={String(data.moderation_summary?.dj_assigns || 0)} />
                <ReportStat label="Cohost promotions" value={String(data.moderation_summary?.cohost_promotions || 0)} />
                <ReportStat label="Kicks" value={String(data.moderation_summary?.kicks || 0)} />
                <ReportStat label="Removes (banned)" value={String(data.moderation_summary?.removes || 0)} />
                <ReportStat label="Renames" value={String(data.moderation_summary?.renames || 0)} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-2">Participants ({(data.participants || []).length})</div>
                <ul className="space-y-1 max-h-48 overflow-y-auto bg-black/30 rounded p-2">
                  {(data.participants || []).slice(0, 100).map((p) => (
                    <li key={p.identity} className="text-xs text-white/70 font-mono flex items-center gap-2">
                      <span className="truncate flex-1">{p.display_name || "—"}</span>
                      <span className="text-[10px] opacity-50">{p.kind}</span>
                      <span className="text-[10px] opacity-50">{p.total_seconds_in_room ? `${Math.round(p.total_seconds_in_room/60)}m` : "—"}</span>
                      {p.was_kicked && <span className="text-[10px] text-amber-300/80">kicked</span>}
                      {p.was_banned && <span className="text-[10px] text-red-300/80">banned</span>}
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to={`/dashboard/rooms/${roomId}/report`}
                className="inline-flex items-center gap-1.5 text-[11px] text-[#00F0FF] hover:underline"
                onClick={onClose}
              >
                Open full host report page →
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ReportStat({ label, value }) {
  return (
    <div className="bg-black/30 rounded p-2.5">
      <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">{label}</div>
      <div className="text-sm font-mono text-white/85 mt-0.5">{value}</div>
    </div>
  );
}
